import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

interface MediaContextValue {
  secondaryImages: SecondaryImage[];
  handleSecondaryUpload: (files: File[], category?: string) => void;
  handleSecondaryRemove: (img: SecondaryImage) => void;
  handleSecondaryCategoryChange: (img: SecondaryImage, newCategory: string) => void;
  resetSecondaryImages: () => void;
}

const MediaContext = createContext<MediaContextValue | null>(null);

export const useMedia = (): MediaContextValue => {
  const ctx = useContext(MediaContext);
  if (!ctx) throw new Error('useMedia must be used within a MediaProvider');
  return ctx;
};

export const MediaProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [secondaryImages, setSecondaryImages] = useState<SecondaryImage[]>([]);

  const handleSecondaryUpload = useCallback(async (files: File[], category?: string) => {
    const items: SecondaryImage[] = files.map((f, i) => ({
      id: `${Date.now()}-${i}`,
      file: f,
      url: URL.createObjectURL(f),
      alt: f.name,
      category: category || 'reference',
    }));
    setSecondaryImages(prev => [...prev, ...items]);
  }, []);

  const handleSecondaryRemove = useCallback(async (img: SecondaryImage) => {
    if (img.url.startsWith('blob:')) URL.revokeObjectURL(img.url);
    setSecondaryImages(prev => prev.filter(x => x.id !== img.id));
  }, []);

  const handleSecondaryCategoryChange = useCallback((img: SecondaryImage, newCategory: string) => {
    setSecondaryImages(prev => prev.map(x =>
      x.id === img.id ? { ...x, category: newCategory } : x
    ));
  }, []);

  const resetSecondaryImages = useCallback(() => {
    setSecondaryImages([]);
  }, []);

  // Cleanup blob URLs on unmount
  useEffect(() => {
    return () => {
      secondaryImages.forEach(img => {
        if (img.url.startsWith('blob:')) URL.revokeObjectURL(img.url);
      });
    };
  }, [secondaryImages]);

  return (
    <MediaContext.Provider value={{
      secondaryImages,
      handleSecondaryUpload,
      handleSecondaryRemove,
      handleSecondaryCategoryChange,
      resetSecondaryImages,
    }}>
      {children}
    </MediaContext.Provider>
  );
};
